export const FINANCIAL_AID_REPORT_STALE_CHANNEL = "financial-aid-report-stale";

export const financialAidReportStaleStorageKey = (applicationId: string): string =>
  `financialAidReportStale:${applicationId}`;

export type FinancialAidReportStaleSignal = {
  applicationId: string;
  staleAfter: number;
  emittedAt: number;
  source: "staff-correction";
};

const normalizeEpochSeconds = (value: unknown): number | null => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return Math.floor(numeric > 1_000_000_000_000 ? numeric / 1000 : numeric);
};

const parseFinancialAidReportStaleSignal = (value: unknown): FinancialAidReportStaleSignal | null => {
  let payload = value;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const applicationId = String(record.applicationId ?? "").trim();
  const staleAfter = normalizeEpochSeconds(record.staleAfter);
  if (!applicationId || staleAfter == null) {
    return null;
  }
  return {
    applicationId,
    staleAfter,
    emittedAt: normalizeEpochSeconds(record.emittedAt) ?? staleAfter,
    source: "staff-correction",
  };
};

export const readFinancialAidReportStaleSignal = (
  applicationId: string | null | undefined,
): FinancialAidReportStaleSignal | null => {
  const normalizedApplicationId = String(applicationId ?? "").trim();
  if (!normalizedApplicationId || typeof window === "undefined") {
    return null;
  }
  try {
    const signal = parseFinancialAidReportStaleSignal(
      window.localStorage.getItem(financialAidReportStaleStorageKey(normalizedApplicationId)),
    );
    return signal?.applicationId === normalizedApplicationId ? signal : null;
  } catch {
    return null;
  }
};

export const subscribeFinancialAidReportStaleSignal = (
  applicationId: string | null | undefined,
  onSignal: (signal: FinancialAidReportStaleSignal) => void,
): (() => void) => {
  const normalizedApplicationId = String(applicationId ?? "").trim();
  if (!normalizedApplicationId || typeof window === "undefined") {
    return () => undefined;
  }

  const storageKey = financialAidReportStaleStorageKey(normalizedApplicationId);
  const emitIfMatched = (value: unknown): void => {
    const signal = parseFinancialAidReportStaleSignal(value);
    if (signal?.applicationId === normalizedApplicationId) {
      onSignal(signal);
    }
  };
  const handleStorage = (event: StorageEvent): void => {
    if (event.key === storageKey) {
      emitIfMatched(event.newValue);
    }
  };
  const handleCustomEvent = (event: Event): void => {
    emitIfMatched((event as CustomEvent<unknown>).detail);
  };
  let channel: BroadcastChannel | null = null;

  window.addEventListener("storage", handleStorage);
  window.addEventListener(FINANCIAL_AID_REPORT_STALE_CHANNEL, handleCustomEvent);
  try {
    if (typeof BroadcastChannel === "function") {
      channel = new BroadcastChannel(FINANCIAL_AID_REPORT_STALE_CHANNEL);
      channel.onmessage = (event) => emitIfMatched(event.data);
    }
  } catch {
    channel = null;
  }

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(FINANCIAL_AID_REPORT_STALE_CHANNEL, handleCustomEvent);
    try {
      channel?.close();
    } catch {
      // ignore browser-managed channel lifecycle errors
    }
  };
};

export const publishFinancialAidReportStaleSignal = (input: {
  applicationId: string | null | undefined;
  staleAfter: number | null | undefined;
}): void => {
  const applicationId = String(input.applicationId ?? "").trim();
  const staleAfter = normalizeEpochSeconds(input.staleAfter);
  if (!applicationId || staleAfter == null || typeof window === "undefined") {
    return;
  }

  const payload: FinancialAidReportStaleSignal = {
    applicationId,
    staleAfter,
    emittedAt: Math.floor(Date.now() / 1000),
    source: "staff-correction",
  };

  try {
    window.localStorage.setItem(
      financialAidReportStaleStorageKey(applicationId),
      JSON.stringify(payload),
    );
  } catch {
    // Stale-report marking is best effort; the save itself must not depend on browser storage.
  }

  try {
    window.dispatchEvent(new CustomEvent(FINANCIAL_AID_REPORT_STALE_CHANNEL, { detail: payload }));
  } catch {
    // ignore unsupported CustomEvent/window lifecycle edge cases
  }

  try {
    if (typeof BroadcastChannel === "function") {
      const channel = new BroadcastChannel(FINANCIAL_AID_REPORT_STALE_CHANNEL);
      channel.postMessage(payload);
      channel.close();
    }
  } catch {
    // ignore blocked broadcast APIs
  }
};
